/*       */

import uuidv4 from 'uuid/v4';

import { get } from 'app/utils/lo/lo';
import Cache from 'app/utils/cache/Cache';
import { isDev } from 'app/utils/env';

class ChatSocket {
    socket = {};
    url = '';
    token = '';
    userId = '';
    handleErrorClbk = null;
    subscriptions;
    reconnectAttempts = 0;
    maxReconnectAttempts = 5;
    reconnectTimeout = null;
    isConnected = false;
    isConnecting = false;
    getTokenCallback = null;

    constructor(url, { userId, token, getToken }) {
        if (!url || !userId || !token) {
            throw new Error('wrong arguments.');
        }
        this.url = url;
        this.token = token;
        this.userId = userId;
        this.getTokenCallback = getToken;
        this.subscriptions = this._buildSuscribtions();
        this.resultHandlers = new Cache();
        this.resultHandlers.onExpire = (key, handler) => {
            handler.reject(new Error('chat result timeout'));
        };
        this._setupVisibilityHandler();
    }

    _setupVisibilityHandler() {
        if (typeof document !== 'undefined') {
            this._handleVisibilityChange = () => {
                if (!document.hidden && !this.isConnected && !this.isConnecting) {
                    console.log('[ChatAPI] Page visible, reconnecting...'); // eslint-disable-line no-console
                    this.reconnectAttempts = 0;
                    this._attemptReconnect();
                }
            };
            this._handleOnline = () => {
                if (!this.isConnected && !this.isConnecting) {
                    console.log('[ChatAPI] Network online, reconnecting...'); // eslint-disable-line no-console
                    this.reconnectAttempts = 0;
                    this._attemptReconnect();
                }
            };
            document.addEventListener('visibilitychange', this._handleVisibilityChange);
            window.addEventListener('online', this._handleOnline);
        }
    }

    _attemptReconnect() {
        if (this.isConnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
            return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 10000);

        console.log(`[ChatAPI] Reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`); // eslint-disable-line no-console

        this.reconnectTimeout = setTimeout(() => {
            if (this.handleErrorClbk) {
                this.handleErrorClbk();
            }
        }, delay);
    }

    _buildSuscribtion(name, events) {
        return events.map(event => ({
            id: this.generateUniqueId(),
            msg: 'sub',
            name,
            params: [event, true],
        }));
    }

    _buildSuscribtions() {
        return [
            ...this._buildSuscribtion('stream-notify-user', [
                `${this.userId}/message`,
                `${this.userId}/otr`,
                `${this.userId}/webrtc`,
                `${this.userId}/notification`,
                `${this.userId}/rooms-changed`,
                `${this.userId}/subscriptions-changed`,
                `${this.userId}/audioNotification`,
            ]),
            ...this._buildSuscribtion('stream-notify-logged', [
                'Users:NameChanged',
                'Users:Deleted',
                'deleteEmojiCustom',
                'updateEmojiCustom',
                'updateAvatar',
                'permissions-changed',
            ]),
            ...this._buildSuscribtion('stream-notify-all', [
                'roles-change',
                'updateEmojiCustom',
                'deleteEmojiCustom',
                'updateAvatar',
                'public-settings-changed',
                'permissions-changed',
                'updateCustomSound',
                'deleteCustomSound',
            ]),
        ];
    }

    connect = () => {
        // $FlowFixMe
        return new Promise((resolve, reject) => {
            if (this.isConnecting) {
                console.warn('[ChatAPI] Already connecting...'); // eslint-disable-line no-console
                return;
            }

            this.isConnecting = true;
            this.socket = new WebSocket(this.url);
            this.socket.onopen = event => {
                this.sendMessage({
                    msg: 'connect',
                    version: '1',
                    support: ['1'],
                });
                this.onMessage(({ data, originalEvent }) => {
                    if (data.msg === 'connected') {
                        this.sendMessage({
                            msg: 'method',
                            method: 'login',
                            id: 'LOGIN',
                            params: [{ resume: this.token }],
                        });
                    }
                    if (data.id === 'LOGIN') {
                        if (get(data, 'params[0].resume') === this.token || get(data, 'result.token') === this.token) {
                            this.subscriptions.forEach(sub => {
                                this.sendMessage(sub);
                            });
                            this.isConnected = true;
                            this.isConnecting = false;
                            this.reconnectAttempts = 0;
                            console.log('[ChatAPI] connected'); // eslint-disable-line no-console
                            resolve({ data, originalEvent });
                        } else if (data.error) {
                            this.isConnecting = false;
                            const errorMsg = data.error.message || '';
                            console.error('[ChatAPI] ERROR', errorMsg); // eslint-disable-line no-console

                            if (errorMsg.includes('Invalid user') && this.getTokenCallback) {
                                console.log('[ChatAPI] Refreshing token and retrying...'); // eslint-disable-line no-console
                                const newToken = this.getTokenCallback();
                                if (newToken && newToken !== this.token) {
                                    this.token = newToken;
                                    this.reconnectAttempts = 0;
                                    setTimeout(() => this._attemptReconnect(), 1000);
                                    return;
                                }
                            }

                            reject(data.error.message);
                        }
                    }
                });
            };
            this.socket.onerror = err => {
                this.isConnected = false;
                this.isConnecting = false;
                console.error('[ChatAPI] CONNECTION ERROR', err); // eslint-disable-line no-console
                this._attemptReconnect();
                reject(err);
            };
            this.socket.onclose = err => {
                this.isConnected = false;
                this.isConnecting = false;
                console.error('[ChatAPI] CONNECTION CLOSED', err); // eslint-disable-line no-console
                if (err.code !== 1000) {
                    this._attemptReconnect();
                }
                reject(err);
            };
        });
    };

    sendMessage = async msg => {
        if (this.socket.readyState === this.socket.OPEN) {
            await this.socket.send(JSON.stringify(msg));
        } else {
            console.warn(`[ChatAPI] SOCKET IS NOT READY for 'sendMessage'.`); // eslint-disable-line no-console
            // Reconnect handling for "webSocket is already in CLOSING or CLOSED state" error.
            if (!this.socket.CONNECTING && this.socket.readyState) {
                console.warn(`[ChatAPI] reconnect....`); // eslint-disable-line no-console
                this.clearExistingTimeouts();
                setTimeout(() => { if (this.handleErrorClbk) this.handleErrorClbk(); }, 1000);
            }
        }
    };

    onMessage = callback => {
        this.socket.onmessage = event => {
            const data = JSON.parse(event.data);
            const { id, msg, result, error } = data || {};

            if (msg === 'ping') {
                return this.sendMessage({ msg: 'pong' });
            }

            if (msg === 'result') {
                const handler = this.resultHandlers.del(id);
                if (handler) {
                    const { resolve, reject } = handler;
                    if (error) {
                        const message = error.message || 'Request failed.';
                        reject(new Error(message));
                    } else {
                        resolve(result);
                    }
                }
            }

            return callback({ data, originalEvent: event });
        };
    };

    onError = callback => {
        this.socket.onerror = event => {
            this.close();
            return callback();
        };
    };

    onClose = callback => {
        this.socket.onclose = event => {
            this.close();
            return callback();
        };
    };

    close = () => {
        this.clearExistingTimeouts();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this._handleVisibilityChange) {
            document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        }
        if (this._handleOnline) {
            window.removeEventListener('online', this._handleOnline);
        }
        this.isConnected = false;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        if (this.socket && this.socket.close) {
            this.socket.close(1000, 'Client closed');
        }
        this.socket = {};
    };

    /*
     * @param roomId (number) - The room ID
     * @param max (number) - the message quantity
     */
    getRoomHistory = (roomId, rel, max, startTimestamp, endTimestamp) => {
        isDev && console.debug('[dev] chat getRoomHistory', rel); // eslint-disable-line no-console

        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('getRoomHistory');
            const startDate = startTimestamp ? { $date: parseInt(startTimestamp) } : null;
            const endDate = { $date: endTimestamp ? parseInt(endTimestamp) : 0 };

            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    msg: 'method',
                    method: 'loadHistory',
                    id,
                    params: [roomId, startDate, max || 0, endDate, false],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * @param rid (string) - Chat Room ID
     */
    addUsersToRoom = (rid, users) => {
        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('addUsersToRoom');
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    msg: 'method',
                    method: 'addUsersToRoom',
                    id,
                    params: [{ rid, users }],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * Mark a thread as read (clears tunread on the subscription server-side).
     * @param tmid (string) - thread parent message id
     */
    readThreads = tmid => {
        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('readThreads');
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    msg: 'method',
                    method: 'readThreads',
                    id,
                    params: [tmid],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * get room mentions
     * @param id (string) - Chat Room ID
     * @param searchString (string) - Search String
     * @param exclude (array) - Exclude Usernames
     */
    getRoomSpotlight = (rid, searchString, exclude = []) => {
        const id = this.generateUniqueId('spotlight');
        return new Promise((resolve, reject) => {
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    msg: 'method',
                    method: 'spotlight',
                    params: [
                        searchString,
                        exclude,
                        {
                            users: true,
                            mentions: true,
                        },
                        rid,
                    ],
                    id,
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * get chat subscriptions
     */
    getSubscriptions = () => {
        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('subscriptions');
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    msg: 'method',
                    method: 'subscriptions/get',
                    id,
                    params: [],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * @param rid (string) - Chat Room ID
     */
    subscribeStreamRoomMessages = (id, rid) => {
        this.sendMessage({
            msg: 'sub',
            id,
            name: 'stream-room-messages',
            params: [`${rid}`, false],
        });
    };

    /*
     * @param rid (string) - Chat Room ID
     */
    unsubscribeStreamRoomMessages = (id, rid) => {
        this.sendMessage({
            msg: 'unsub',
            id,
            name: 'stream-room-messages',
            params: [`${rid}`, true],
        });
    };

    // Rocket.Chat hard-deletes (Message_KeepHistory: false) publish on
    // stream-notify-room/<rid>/deleteMessage rather than stream-room-messages,
    // so we subscribe per-room to receive deletions for other users.
    subscribeRoomDeleteMessage = (id, rid) => {
        this.sendMessage({
            msg: 'sub',
            id,
            name: 'stream-notify-room',
            params: [`${rid}/deleteMessage`, false],
        });
    };

    unsubscribeRoomDeleteMessage = (id, rid) => {
        this.sendMessage({
            msg: 'unsub',
            id,
            name: 'stream-notify-room',
            params: [`${rid}/deleteMessage`, true],
        });
    };

    generateUniqueId = shortName => {
        return !shortName ? uuidv4() : `${shortName}.${uuidv4()}`;
    };

    /*
     * @param rid (string) - Chat Room ID
     * @param msg (string) - Message
     */
    sendChatMessage = ({ rid, msg }) => {
        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('');
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    id,
                    msg: 'method',
                    method: 'sendMessage',
                    params: [{ _id: this.generateUniqueId(), rid, msg }],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * @param fileId (string) - File ID
     */
    deleteRoomAttachment = fileId => {
        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('deleteFileMessage');
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    msg: 'method',
                    method: 'deleteFileMessage',
                    id,
                    params: [fileId],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    /*
     * @param _id" (string) - Message ID
     * @param rid (string) - Chat Room ID
     * @param msg (string) - Message
     */
    updateChatMessage = data => {
        return new Promise((resolve, reject) => {
            const id = this.generateUniqueId('');
            try {
                this.resultHandlers.set(id, { resolve, reject });
                this.sendMessage({
                    id,
                    msg: 'method',
                    method: 'updateMessage',
                    params: [data],
                });
            } catch (e) {
                this.resultHandlers.del(id);
                reject(e);
            }
        });
    };

    clearExistingTimeouts = () => {
        this.resultHandlers.clearAllTimeout();
    };
}

export default ChatSocket;
